<template>
    <div class="search" :class="[{ 'are-search-results-visible': searchResultsVisible }, {'are-search-suggestions-visible' : searchSuggestionsVisible}]">
        <transition name="slide">
            <search-suggestions v-if="searchSuggestionsVisible" :filterOptions="filterOptions" :suggestions="searchSuggestions" :searchQuery="searchQuery" :results="searchResults" :searchOption="searchOption" />
        </transition>
        <transition name="slide">
            <search-results v-if="searchResultsVisible" :indices="indices" :filterOptions="filterOptions" :results="searchResults" :searchQuery="searchQuery" :productSuggestions="productSuggestions" />
        </transition>
    </div>
</template>

<script>
import * as ProjectConfig from 'Models/utils/project-config';
import fromPairs from 'lodash/fromPairs';
import toPairs from 'lodash/toPairs';
import compact from 'lodash/compact';
import map from 'lodash/map';
import join from 'lodash/join';
import store from 'Store';
import debounce from 'lodash/debounce';
import { mapActions, mapState, mapGetters } from 'vuex';
import { dispatch, listen } from 'Models/utils/event-bus';
import { lockPage, unlockPage } from 'Models/locker';
import { dim, undim } from 'Models/dimmer';
import trackEvent from 'Models/tracking';

export default {
    store,
    props: {
        searchClient: {
            type: String,
            default: 'algolia',
        },
    },
    data () {
        return {
            searchOption: 'all',
            indices: false,
            resultsCategories: false,
            filterOptions: [
                {
                    key: 'all',
                    label: ProjectConfig.get('search.all'),
                },
            ],
        };
    },
    computed: {
        ...mapState([
            'searchResultsVisible',
            'searchLoading',
            'searchQuery',
            'searchResults',
            'searchSuggestions',
        ]),
        ...mapGetters(['isDesktopNavigation', 'isMobileNavigation', 'areSearchResultsVisible', 'areSearchSuggestionsVisible']),
        searchSuggestionsVisible () {
            return this.searchClient === 'factfinder' && this.areSearchSuggestionsVisible;
        },
        searchResultsVisible () {
            if (this.searchClient === 'factfinder') {
                return this.areSearchResultsVisible && !this.areSearchSuggestionsVisible;
            } else {
                return this.areSearchResultsVisible;
            }
        },
        productSuggestions () {
            if (this.searchSuggestions && this.searchSuggestions.products && this.searchSuggestions.products.length > 0) {
                return this.searchSuggestions.products.slice(0, 4);
            }
            return false;
        },
    },
    watch: {
        searchQuery () {
            var parameters = this.getUrlParameters(window.location.search);

            if (
                this.searchQuery === parameters.searchquery ||
                (!this.searchQuery && !parameters.searchquery)
            ) {
                return;
            }

            if (this.searchQuery) {
                parameters.searchquery = encodeURIComponent(this.searchQuery);
            } else {
                delete parameters.searchquery;
            }

            var newUrl = window.location.origin + window.location.pathname;
            var stringParameters = this.stringifyUrlParameters(parameters);

            if (stringParameters) {
                newUrl += '?' + stringParameters;
            }

            window.history.pushState(null, null, newUrl);
        },
        areSearchResultsVisible () {
            if (this.areSearchResultsVisible !== '' && this.areSearchResultsVisible) {
                lockPage();
                dim(true);
            } else {
                unlockPage();
                undim();
            }
        },
        areSearchSuggestionsVisible () {
            // scrolling to top when switching from suggestions to results
            this.$el.scrollTop = 0;
            console.log('search suggestions visisble');

            if ((this.areSearchSuggestionsVisible !== '' && this.areSearchSuggestionsVisible && this.isMobileNavigation) || (this.areSearchResultsVisible !== '' && this.areSearchResultsVisible)) {
                lockPage();
            } else {
                unlockPage();
            }
        },
        searchResults: {
            handler () {
                this.updateResultAmounts();
            },
            deep: true,
        },
    },
    mounted () {
        store.commit('setSearchClient', this.searchClient);
        store.commit('setSearchResultsActive', this.searchClient === 'algolia');
        store.commit('setSearchSuggestionsActive', this.searchClient === 'factfinder');

        this.getSearchOption();
        this.updateSearchFromUrl();
        this.prepareResultCategories();
        this.updateResultAmounts();

        window.addEventListener('popstate', () => {
            this.updateSearchFromUrl();
        });

        // listen to search query change event
        listen('search-query', payload => {
            this.debouncedSearch(payload);
        });

        listen('searchOptionUpdated', payload => {
            this.searchOption = payload;
        });
    },
    methods: {
        ...mapActions([
            'search',
        ]),
        debouncedSearch: debounce(function (query) {
            if (this.searchOption === 'all') {
                this.search({ query: query });
            } else {
                store.commit('setSearchQuery', query);
            }
            // track the search
            this.debouncedTracking(query);
        }, 1000),
        debouncedTracking: debounce(function (query) {
            trackEvent('Search', 'Send', query, window.location.href);
        }, 3500),
        updateSearchFromUrl () {
            var parameters = this.getUrlParameters(window.location.search);

            if (parameters.searchquery) {
                // open search both on mobile and desktop, in case of resize
                dispatch('open-search-desktop');
                dispatch('toggle-search-mobile');
                this.search({ query: parameters.searchquery, showSuggestions: this.searchOption === 'all' });
            }
        },
        getUrlParameters (queryString, shouldDecode = true) {
            if (queryString.indexOf('?') === 0) {
                queryString = queryString.substring(1);
            }

            if (shouldDecode) {
                queryString = decodeURIComponent(queryString);
            }

            return fromPairs(
                compact(
                    map(queryString.split('&'), function (item) {
                        if (item) return item.split('=');
                    }),
                ),
            );
        },
        stringifyUrlParameters (parameters) {
            return join(
                map(toPairs(parameters), item => (item[1] ? join(item, '=') : item[0])),
                '&',
            );
        },
        prepareResultCategories () {
            this.indices = ProjectConfig.get(`${this.searchClient}.indices`);
            this.indices.forEach(index => {
                this.filterOptions.push(index);
            });
        },
        updateResultAmounts () {
            this.filterOptions.forEach(option => {
                const amount = this.getResultsAmount(option.key);
                this.$set(option, 'amount', amount);
            });
        },
        getResultsAmount (key) {
            if (key === 'all') {
                let amount = 0;
                this.indices.forEach(index => {
                    amount += this.getResultsAmount(index.key);
                });
                return amount;
            } else {
                return this.searchResults[key] ? this.searchResults[key].length : 0;
            }
        },
        getSearchOption () {
            let existingSearchOption = window.localStorage.getItem('recaSelectedSearchOption');
            if (existingSearchOption !== '' && existingSearchOption !== null) {
                existingSearchOption = JSON.parse(existingSearchOption);
                this.searchOption = existingSearchOption.value;
            }
        },
    },
};
</script>

<style lang="scss">
@import '~sass-mq';

.search {
    $block: &;

    position: fixed;
    z-index: 199;
    left: 0;
    width: 100%;
    pointer-events: none;

    top: calc(#{$header-height-mobile} * 2);
    height: calc((var(--vh, 1vh) * 100) - (#{$header-height-mobile} * 2));
    overflow: auto;
    scroll-behavior: smooth;

    .browser-explorer & {
        height: calc(100vh - (#{$header-height-mobile} * 2));
    }

    @include mq(tablet-landscape) {
        top: $header-height-expanded-desktop;
        height: calc((var(--vh, 1vh) * 100) - #{$header-height-expanded-desktop});

        .browser-explorer & {
            height: calc(100vh - #{$header-height-expanded-desktop});
        }
    }

    @include mq($until: tablet-landscape) {
        z-index: 999;
    }

    &.are-search-results-visible {
        pointer-events: auto;
    }

    &.are-search-suggestions-visible {
        pointer-events: auto;

        @include mq(tablet-landscape) {
            z-index: 201;
            top: 60px;
            height: calc((var(--vh, 1vh) * 100) - 60px);

            .browser-explorer & {
                height: calc(100vh - 60px);
            }
        }
    }
}
</style>
